// English locale — mirrors the es.ts key structure exactly.
// Keep entries in the same order as es.ts so diffs stay readable.

export const en = {
    cron: {
      daily:            "every day at {at}",
      weekdays:         "Monday to Friday at {at}",
      weekends:         "weekends at {at}",
      on_days:          "{days} at {at}",
      monthly:          "day {day} of each month at {at}",
      every_hour:       "every hour, at :{minute}",
      every_n_hours:    "every {n} hours, at :{minute}",
      every_n_minutes:  "every {n} minutes",
      every_minute:     "every minute",
      every_label:      "every N hours instead",
      use_cron:         "edit as cron",
      use_picker:       "use the picker",
      preset_every_day: "every day",
      preset_weekdays:  "weekdays",
      preset_weekends:  "weekends",
      day_short: { sun: "Sun", mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat" },
    },
    when: {
      now:     "now",
      in:      "in {amount}",
      ago:     "{amount} ago",
      minutes: "{n} min",
      hours:   "{n} h",
      days:    "{n} d",
    },
  common: {
    loading:      "Loading…",
    saving:       "Saving…",
    cancel:       "Cancel",
    save:         "Save",
    delete:       "Delete",
    edit:         "Edit",
    create:       "Create",
    add:          "Add",
    remove:       "Remove",
    reload:       "Reload",
    shutdown:     "Shut down",
    enabled:      "Enabled",
    disabled:     "Disabled",
    enable:       "Enable",
    disable:      "Disable",
    open:         "Open",
    close:        "Close",
    confirm:      "Confirm",
    optional:     "(optional)",
    none:         "—",
    none_yet:     "Nothing here yet.",
    error_generic:"Something went wrong.",
    search:       "Search",
    new:          "New",
    restore:      "Restore",
    show:         "Show",
    hide:         "Hide",
    copy:         "Copy",
    run:          "Run",
    refresh:      "Refresh",
    view_all:     "View all",
    saved:        "Saved.",
    deleted:      "Deleted.",
    pager_prev:   "Previous",
    pager_next:   "Next",
    pager_page:   "Page {page} of {total}",
    pager_range:  "{from}–{to} of {total}",
    pager_per_page:"Per page",
  },
  daemon: {
    connecting:    "Connecting to the daemon…",
    unreachable:   "Could not reach the daemon at localhost:7430.",
    unreachable_hint:"Start APX with `apx daemon start` and refresh.",
    version:       "Version",
    uptime:        "Uptime",
    status:        "Status",
    running:       "running",
    down:          "down",
    reload_hint:   "POST /admin/reload — reloads ~/.apx/config.json without restarting.",
    shutdown_confirm: "Shut down the daemon? Upcoming requests will fail until it restarts.",
    shutdown_done: "Daemon stopped.",
  },
  pairing: {
    title:        "Pair this device",
    subtitle:     "You are connecting from outside this machine. For security, pair this browser with a pairing code.",
    steps_title:  "How to get the code",
    step_1:       "On the PC where APX is running, open a terminal.",
    step_2:       "Run `apx pair` (or scan the QR with APX Deck).",
    step_3:       "Copy the code shown below the QR and paste it here.",
    code_label:   "Pairing code",
    code_ph:      "e.g. 7f3a1c9e-…",
    label_label:  "Device name",
    label_ph:     "e.g. Living room laptop",
    submit:       "Pair",
    linking:      "Pairing…",
    success:      "Device paired ✓",
    err_required: "Paste the pairing code.",
    err_expired:  "The code expired. Run `apx pair` again and try again.",
    err_unknown:  "Unknown or already-used code. Generate a new one with `apx pair`.",
    err_generic:  "Could not pair. Check the code and try again.",
    revoke_hint:  "You can revoke this device at any time from Settings or with `apx pair revoke`.",
  },
  nav: {
    apx_admin:    "APX",
    settings:     "Settings",
    project:      "Project",
    add_project:  "Add project",
    all_projects:      "All projects",
    more_projects:     "{count} more",
    collapse_projects: "Hide projects",
    expand_projects:   "Show projects",
    modules: {
      voice:   "Voices",
      desktop: "Desktop",
      deck:  "Deck",
      code:  "Code",
      web:   "Web",
    },
  },
  topbar: {
    breadcrumb_root:     "APX",
    breadcrumb_settings: "APX › Settings",
    breadcrumb_project:  "APX › Project",
    breadcrumb_base:     "Base",
    breadcrumb_projects: "Projects",
    light:               "Switch to light",
    dark:                "Switch to dark",
    lang_toggle:         "Language",
  },
  admin: {
    title:             "APX",
    subtitle:          "Admin panel. Global config, channels and projects.",
    engines_title:     "Engines",
    engines_subtitle:  "Available LLM adapters. API keys live in ~/.apx/config.json.",
    telegram_title:    "Telegram",
    telegram_subtitle: "Configured channels. Each one can be pinned to a project.",
    telegram_polling_on:  "Polling active",
    telegram_polling_off: "Disabled",
    telegram_add_channel: "Channel",
    telegram_send_test:   "Test",
    telegram_send_test_title: "Send to",
    telegram_default_message: "Test message from APX panel ✅",
    projects_title:    "Registered projects",
    projects_subtitle: "Click a project to open its panel.",
    unregister:        "Unregister",
    unregister_confirm:"Remove {label} from APX? The folder is not deleted; only unregistered.",
    reload_success:    "Config reloaded.",
    telegram_polling_started: "Polling started.",
    telegram_polling_stopped: "Polling stopped.",
    telegram_channel_removed: "Channel deleted.",
    agents_badge:      "agents",
    engine_badge:      "yes",
    engine_badge_no:   "no",
    base_label:        "Base",
  },
  add_project: {
    title:       "Add project",
    subtitle:    "APX will index .apc/, agents and AGENTS.md in that folder.",
    path_label:  "Absolute path",
    path_hint:   "Equivalent to apx project add /path/to/project",
    path_placeholder: "/path/to/my-project",
    register:    "Register",
    path_required: "Path required.",
    registered:  "Project #{id} registered.",
    search_btn:  "Browse",
    picker_prompt: "Pick the project folder",
    browser_unavailable: "Browser unavailable until daemon restarts. Paste path manually.",
    no_folders:  "No folders.",
  },
  inbox: {
    search: "Search",
    no_match: "Nothing matches",
    hide_quiet: "Hide quiet agents",
    open_in_project: "Open in project",
    super_agent_scope: "All projects",
    title: "Agent inbox",
    subtitle:
      "Every agent as a conversation, most recent first.",
    empty: "You have not talked to any agent yet.",
    pinned: "lead",
    show_quiet: "Show quiet agents",
    no_reply_yet: "(no replies yet)",
  },

  settings: {
    title:       "Settings",
    subtitle:    "Panel preferences + local daemon diagnostics.",
    appearance:  "Appearance",
    light_mode:  "Light",
    dark_mode:   "Dark",
    system_mode: "System",
    language:    "Language",
    daemon:      "Daemon",
    daemon_sub:  "Status of the local process that serves this web and orchestrates agents.",
    engines:     "Available engines",
    engines_sub: "LLM adapters compiled with the daemon.",
    token:       "Session token",
    token_sub:   "If this web could not auto-load the token, paste it here.",
    token_active:"(token already active)",
    token_paste: "Paste daemon bearer",
    token_saved: "Token saved.",
    devices:     "Paired devices",
    devices_sub: "GET /pair/list. Revoking invalidates that bearer on the daemon.",
    devices_empty: "No paired clients yet.",
    devices_revoke_confirm: "Revoke client {id}?",
    devices_revoke_success: "Client revoked.",
    devices_pair_btn:   "Pair device",
    devices_pair_title: "Pair device",
    devices_pair_desc:  "Scan the QR with your phone camera to open the web already paired, or paste the code on another PC.",
    devices_pair_scan:  "Scan with your phone camera — opens the web already paired.",
    devices_pair_code:  "Or paste this code on the pairing screen:",
    devices_pair_url:   "Access URL",
    devices_pair_link:  "Or copy this link and open it on the other device (enters automatically):",
    devices_pair_copy:   "Copy",
    devices_pair_copied: "Link copied to clipboard.",
    devices_pair_copied_code: "Code copied.",
    devices_pair_expires: "Expires in {s}s",
    devices_pair_expired: "The code expired.",
    devices_pair_regen:  "Generate another",
    devices_pair_waiting: "Waiting for device to confirm…",
    devices_pair_done:   "Device paired ✓",
    devices_pair_localhost_only: "Codes can only be generated from the daemon's PC (localhost).",
    devices_last_seen:   "seen:",
    devices_never:       "never",
    devices_revoke:      "Revoke",
    account_section:     "Account",
    agents_section:      "Agents & models",
    super_agent_section: "Super-agent",
    knowledge_section:   "Models & knowledge",
    channels_section:    "Channels & devices",
    modules_section:     "Modules",
    advanced_section:    "Advanced",

    tabs: {
      identity: "Identity",
      super_agent: "Super-agent",
      profile: "Agent profile",
      engines: "Engines & models",
      telegram: "Telegram",
      devices: "Devices",
      nudge: "Interruptions",
      advanced: "Advanced",
    },

    profile: {
      title: "Agent profile",
      subtitle:
        "An installable line of work for the super-agent: what it does with its day and when it speaks to you. Distinct from the agent's name (that lives under Identity).",
      active_hint:
        "A profile is active: its block ships on every turn of every channel. Deactivate to go back to vanilla.",
      vanilla_hint:
        "No profile is active. APX behaves exactly as it always has — the super-agent prompt is identical to a clean install.",
      none_available: "No profiles available yet.",
      active: "active",
      activate: "Activate",
      replace_active: "Replace the active one",
      deactivate: "Deactivate",
      on: "Active",
      off: "Inactive",
      replaces_active: "Turning this on replaces the active profile",
      deactivate_title: "Deactivate the profile?",
      deactivate_confirm:
        "APX goes back to vanilla. The profile's routines are disabled but not deleted, and your settings, tasks and memory are untouched — activating it again restores everything.",
      activated: "Profile active",
      deactivated: "Profile off — APX is back to vanilla",
      token_cost: "Prompt cost",
      over_budget: "over its declared budget",
      settings_title: "Profile settings",
      settings_subtitle:
        "Blank fields fall back to the package default. Changing a time really reschedules the routine.",
      saved: "Settings saved",
      saved_with_routines: "Settings saved and routines rescheduled",
      doctor_title: "Doctor",
      doctor_clean: "All good.",
      preview_title: "Prompt block",
      preview_subtitle: "Exactly what reaches the model, with your values substituted.",
      preview_empty: "(empty)",
      preview_inactive: "This is what the model would receive if you activated this profile.",
      no_settings: "This profile has nothing to configure.",
      settings_locked: "Activate the profile to change its settings.",
      doctor_vanilla: "No profile active. APX behaves as it always has.",
    },

    nudge: {
      title: "Interruption budget",
      subtitle:
        "How often APX may write to you without being asked. Replies to your own messages are never held back.",
      off_hint:
        "The budget is off: every unrequested message goes out. Everything is still recorded below.",
      on_hint: "{{sent}} sent today, {{left}} left.",
      source: "Set by",
      enabled: "Enforce the budget",
      daily_max: "Messages per day",
      daily_max_hint: "0 means no ceiling.",
      quiet_hours: "Quiet hours",
      quiet_hours_hint: "HH:MM-HH:MM. Crosses midnight fine. Leave empty for none.",
      cooldown: "Minimum gap (minutes)",
      project_cooldown: "Minimum gap per project (minutes)",
      kind_cooldown: "Minimum gap per kind (minutes)",
      critical_bypass: "Critical messages may bypass the budget",
      critical_bypass_hint: "Bypasses are always recorded and flagged.",
      log_title: "What it has sent",
      log_subtitle: "Unrequested messages only, newest first. Your rating feeds back into what it sends next.",
      log_empty: "Nothing sent unprompted yet.",
      useful: "Useful",
      noise: "Not useful",
      bypass: "bypassed the budget",
      saved: "Budget updated",
      unrated: "not rated",
    },

    identity: {
      title: "Identity",
      subtitle: "User data. Agent configuration goes in Super-agent.",
      agent_name: "Agent name",
      owner_name: "Your name",
      personality: "Personality",
      owner_context: "Owner context",
      owner_context_hint: "Who you are, what you work on, what the agent should know about you.",
      language: "Preferred language",
      timezone: "Timezone (IANA)",
      timezone_hint: "Auto-detected — search to change.",
      saved: "Identity saved.",
    },

    super_agent: {
      title: "Super-agent",
      subtitle: "Personality, model, prompt and modes of the super-agent.",
      personality: "Personality",
      model: "Active model",
      model_hint: "E.g.: anthropic:claude-sonnet-4.5, ollama:gemma2:9b",
      permission_mode: "Permission mode",
      system: "Extra prompt (system)",
      system_hint: "Text prepended to the base system prompt.",
      system_ph: "(Empty = the base prompt from core/agent/prompts/super-agent-base.md is used)",
      fallback_title: "Fallback chain",
      fallback_hint: "If the active model fails, these are tried in order.",
      fallback_add: "Add model to chain",
      saved: "Super-agent saved.",
      enabled_label: "Super-agent enabled",
      model_active:  "Active model (router)",
      model_configure: "Configure in Models",
      behavior_subtitle: "Super-agent behavior. Model and fallback chain are configured in the Model Router.",
    },

    engines_keys: {
      title: "Model API keys",
      subtitle: "Each engine stores its key in ~/.apx/config.json. Already-set values show a safe suffix.",
      ollama_url: "Ollama URL",
      ollama_hint: "Default: http://127.0.0.1:11434",
      key_label: "API key",
      key_placeholder: "(not set)",
      clear: "Clear key",
      saved: "Key saved.",
      cleared: "Key cleared.",
    },

    telegram_global: {
      title: "Telegram (default)",
      subtitle: "Default channel — projects can override with their own channel.",
      bot_token: "Bot token",
      chat_id: "Default chat ID",
      poll_interval: "Poll interval (ms)",
      respond_with_engine: "Respond with engine",
      enabled: "Polling enabled",
      saved: "Telegram saved.",
    },

    advanced: {
      title: "Advanced",
      subtitle: "Raw editor for ~/.apx/config.json. Secrets show as *** set *** but you can write a new one.",
      write: "Apply changes",
      written: "Config applied and daemon reloaded.",
      reload_success: "Config reloaded.",
    },
  },
  project: {
    not_found:          "Roby couldn't find project {pid}: it may have been unregistered, or the ID is wrong.",
    rebuild:            "Rebuild context",
    rebuild_done:       "Rebuild OK.",
    unregister_confirm: "Unregister {label}? The folder is not deleted.",
    unregistered:       "Unregistered.",
    base_subtitle:      "General workspace · super-agent",

    danger: {
      title:                     "Danger zone",
      subtitle:                  "Actions that affect APX's project registry. They do not touch repo files.",
      rebuild_desc:              "Re-scans .apc/, MCPs and agents and regenerates the super-agent context for this project.",
      unregister_desc:           "Removes the project from APX's registry. The folder on disk stays intact.",
      rebuild_confirm_title:     "Rebuild context",
      rebuild_confirm_desc:      "Regenerate context for {label}.",
      rebuild_long:              "Re-reads APC config, lists available MCPs and agents, and rebuilds the super-agent system prompt. Safe to run — nothing is deleted. Use it after editing .apc/ by hand or if changes are not being picked up.",
      unregister_confirm_title:  "Unregister project",
      unregister_long:           "The project disappears from `apx`. Files on disk (.apc/, code, everything) stay. You can re-register it with `apx project register <path>`.",
    },

    nav: {
      overview: "Overview",
      chat:     "Chat",
      config:   "Config",
      telegram: "Telegram",
      agents:   "Agents",
      routines: "Routines",
      tasks:    "Tasks",
      commitments: "Commitments",
      mcps:     "MCPs",
      artifacts: "Artifacts",
      vars:     "Variables",
      logs:     "Logs",
      memories: "Memories",
      structure: "Structure",
      docs:     "Docs",
      files:    "Files",
    },

    sections: {
      workspace: "Workspace",
      content: "Content",
      automation: "Automation",
      knowledge: "Conversations",
      config: "Config",
    },

    overview: {
      tasks_open: "Open tasks",
      routines:   "Routines",
      routines_active: "Active routines",
      agents:     "Agents",
      mcps:       "MCPs",
      artifacts:  "Artifacts",
      chat:       "Chat (super-agent)",
      chat_value: "open",
      roster:     "Team",
      no_agents:  "No agents yet.",
      orchestrators: "Orchestrators",
      specialists: "Specialists",
      recent_tasks: "Recent tasks",
      no_activity: "No open tasks.",
      brain_title: "Team brain",
      brain_desc: "The whole agent map — orchestrators at the core, their specialists clustered around them. Click a node to open it.",
      brain_core: "Team",
    },

    artifacts: {
      title:    "Artifacts",
      subtitle: "Reusable scripts and files stored under the project. Agents create them; you can view, run, rename or delete them.",
    },

    chat: {
      title:       "Chat with agent",
      subtitle:    "Direct conversations with project agents. The super-agent does not intervene.",
      live_title:  "Chat with {agent}",
      superagent_title:  "Chat with {persona}",
      superagent_subtitle: "Chat with {persona} — the APX super-agent. Can use tools (projects, tasks, mcps, agents).",
      loaded_subtitle: "Loaded conversation with {slug}. Sending will append to this thread.",
      thread_subtitle: "History with {persona} on {channel}. Replying here continues the conversation from the web.",
      empty:       "Send a message to start the conversation.",
      placeholder: "Type something and press enter to send (shift+enter = new line)",
      send:        "Send",
      stop:        "Stop",
      new_session: "New session",
      delete:      "Delete",
      delete_confirm_title: "Delete chat",
      delete_confirm_desc:  "This can't be undone. This chat's history will be permanently deleted.",
      deleted:     "Chat deleted.",
      meta_created: "Created {date} · {channel}",
      meta_new:     "New chat · {channel}",
      copy:        "copy",
      copied:      "Copied.",
      stopped_marker: " [stopped]",
      create_agent:   "Create agent",
      create_agent_title: "Create agent",
      create_agent_desc: "Required to start a chat in this project.",
      role_label:  "role",
      model_label: "model",
      model_hint:  "e.g. openai:gpt-5, groq:llama-3.3-70b-versatile",
      master_label: "Master agent",
      list: {
        title:         "Chats",
        new:           "New",
        search:        "Search chats…",
        all_agents:    "All agents",
        empty:         "No conversations yet. Start one from the right.",
        count:         "{n} total",
        pick_agent:    "Pick an agent",
      },
    },

    tasks: {
      title:       "Tasks (TODOs)",
      subtitle:    "Append-only JSONL in ~/.apx/projects/<id>/tasks/.",
      add:         "add",
      add_label:   "New task",
      add_placeholder: "e.g. fix scroll bug",
      empty:       "No {state} tasks.",
      empty_open:  "No open tasks.",
      created:     "Task created.",
      create_error: "could not create task",
      done:        "✓ done",
      drop:        "✗ drop",
      reopen:      "↻ reopen",
      due:         "due",
      via:         "via",
      aria_done:   "mark done",
      aria_drop:   "discard task",
      aria_reopen: "reopen task",
    },

    global_tasks: {

      add: "Add",

      add_title: "New task",

      field_title: "Task",

      field_project: "Project",

      field_due: "Due",
      any_status: "any status",
      title:       "Tasks (all projects)",
      subtitle:    "Aggregated tasks from all registered projects.",
      empty:       "No tasks.",
      due:         "due",
      go_project:  "Go to project",
    },

    commitments: {

      add: "Add",

      add_title: "Record a promise",

      add_hint: "Something you told a specific person you would do. Tasks live next door.",

      field_who: "Who is waiting",

      field_who_hint: "A name as you would say it — free text, not a contact.",

      field_what: "What you promised",

      field_what_ph: "send the revised quote",

      field_due: "By when",

      field_due_hint: "Optional, but it is the part that makes this useful.",

      field_project: "Project",
      title:    "Commitments",
      subtitle: "What was promised, to whom, and by when. Separate from tasks: someone is waiting on these.",
      empty:    "Nothing promised.",
      overdue:  "was due",
      overdue_only: "Past their date",
      no_date:  "no date agreed",
      moved:    "moved",
      mark_kept:   "Kept",
      mark_missed: "Missed",
      state: {
        open:   "open",
        kept:   "kept",
        missed: "missed",
        all:    "all",
      },
    },

    routines: {
      title:       "Heartbeats / Routines",
      subtitle:    "Cron, every:Nm, once:ISO. Each routine fires an agent or a shell.",
      empty:       "No routines. Create one above.",
      new:         "new",
      new_btn:     "New",
      delete_confirm: "Delete routine {name}?",
      delete_confirm_body: "This can't be undone.",
      saved:       "Routine saved.",
      paused:      "paused",
      next_run:    "next:",
      last_run:    "last:",
      enabled_hint: "Active · runs on schedule",
      disabled_hint: "Paused · only via Run button",
      enabled_label: "Enabled",
      new_title:   "New routine",
      edit_title:  "Edit {name}",
      dialog_desc: "Saved in .apc/routines.json. The routine runs while the daemon is active.",
      name_field:   "Name",
      name_no_edit: "Cannot be changed when editing.",
      kind_field:   "Action (kind)",
      schedule_field: "Interval (schedule)",
      schedule_hint: "Choose a preset or type manually. Manual = only runs via Run button.",
      vars_title:   "Available variables",
      what_happens: "What will happen",
      list_title:   "Routines",
      detail_empty: "Pick a routine from the list.",
      edit_btn:     "Edit",
      edit_hint:    "Open the editor: kind, schedule, prompt, pre/post and variables.",
      block_pre:    "Pre-commands",
      block_post:   "Post-commands",
      block_prompt: "Prompt",
      block_text:   "Text",
      block_command: "Command",
      block_empty:  "(empty)",
      runs_title:   "Executions",
      runs_empty:   "No executions yet.",
      runs_close:   "Close",
      runs_no_detail: "No further detail.",
      runs_output:  "Output",
      status_ok:    "ok",
      status_error: "error",
      status_skipped: "skipped",
      agent_field:  "Agent (spec.agent)",
      agent_hint:   "Who executes the routine.",
      agent_loading: "loading…",
      agent_pick:   "— pick an agent —",
      prompt_exec:  "Prompt (spec.prompt)",
      prompt_exec_ph: "what is pending for today?",
      prompt_super: "Prompt (spec.prompt)",
      prompt_super_ph: "summarize the project status",
      pre_field:    "Pre-commands (pre_commands)",
      pre_hint:     "Shell BEFORE the prompt. One per line.",
      post_field:   "Post-commands (post_commands)",
      post_hint:    "Shell AFTER the prompt. One per line.",
      tg_channel:   "Channel (spec.channel)",
      tg_chat_id:   "Chat ID (spec.chat_id)",
      tg_text:      "Telegram Message (spec.text)",
      tg_text_hint: "Fixed message to send. Does not use a model.",
      shell_field:  "Command (spec.command)",
      shell_hint:   "Runs as-is in the shell. No prompt, no pre/post.",
      hb_channel:   "Channel (spec.channel)",
      hb_message:   "Message (spec.message)",
      name_required: "name required",
      save_error:   "save failed",
      run_error:    "run failed",
      toggle_error: "toggle failed",
      delete_error: "delete failed",
      run_success:  "{name} fired.",
      run_confirm:  "Run routine {name} now?",
      run_confirm_body: "Runs the action once, regardless of the schedule.",
      running:      "Running…",
      delete_success: "deleted.",
    },

    agents: {
      title:       "Agents",
      subtitle:    "Defined in .apc/agents/<slug>.md.",
      subtitle_full: "Defined in .apc/agents/<slug>.md. Runtime memory lives under ~/.apx/projects/<id>/agents/<slug>/.",
      empty:       "No agents. Add one with <code>apx agent add</code> or the button.",
      empty_text:  "No agents. Add one with `apx agent add` or the button above.",
      new:         "Agent",
      created:     "Agent {slug} created.",
      slug_invalid: "slug must match /^[a-z][a-z0-9_-]*$/",
      hierarchy:   "Hierarchy",
      list_view:   "List",
      import:      "Import",
      chat:        "Chat",
      view:        "View",
      orchestrator: "Orchestrator",
      new_title:   "New agent",
      new_desc:    "POST /projects/:pid/agents — writes .apc/agents/<slug>.md.",
      slug_label:  "slug",
      slug_ph:     "cody",
      role_label:  "role (optional)",
      role_ph:     "code refactor",
      model_label: "model (optional)",
      model_hint:  "e.g. ollama:gemma2:9b, openai:gpt-4o-mini",
      lang_label:  "language (optional)",
      desc_label:  "description (optional)",
      desc_ph:     "What does this agent do…",
      skills_label: "skills (comma)",
      skills_ph:   "skill-a, skill-b",
      tools_label: "tools (comma)",
      tools_ph:    "tool-a, tool-b",
      parent_label: "reports to (parent, optional)",
      parent_hint: "Sub-agent of an orchestrator.",
      none_parent: "— none —",
      master_label: "Orchestrator (master)",
      create_success: "Agent {slug} created.",
      create_error: "create failed",
      import_title: "Import from vault",
      import_desc: "Templates in ~/.apx/agents. Registered in this project (.apc/agents/<slug>.md).",
      import_empty: "No templates in the vault.",
      import_success: "Imported: {slug}",
      import_already: "already here",
      import_btn:  "Import",
    },

    agent_detail: {
      not_found:       "Agent not found.",
      chat_btn:        "Chat with {slug}",
      reports_to:      "↳ reports to",
      no_threads:      "No threads.",
      no_activity:     "No recorded activity.",
      threads_recent:  "Recent threads",
      subagents:       "Sub-agents",
      subagents_desc:  "Agents that report to this orchestrator.",
      config_title:    "Agent configuration",
      type_label:      "Type",
      area_label:      "Area",
      area_hint:       "e.g. operations, marketing",
      area_ph:         "operations",
      role_label:      "Role",
      parent_label:    "Reports to (parent)",
      none_parent:     "— none —",
      model_label:     "Base model",
      model_hint:      "Empty = uses the Router model (default). Set only to force a model for this agent.",
      model_ph:        "(empty = router default)",
      skills_label:    "Skills (comma)",
      bio_label:       "Bio / description",
      system_label:    "System prompt",
      system_hint:     "Defines personality and behavior (body of AGENT.md).",
      master_label:    "Orchestrator (master)",
      delete_btn:      "Delete agent",
      save_btn:        "Save changes",
      delete_confirm:  "Delete agent \"{slug}\"? Removes .apc/agents/{slug}.md and local runtime data.",
      update_success:  "Agent updated.",
      delete_success:  "Agent deleted.",
      tools_hint:      "Which tools this agent can use. Tap to toggle; or edit the list below.",
      tools_custom_ph: "list (comma): echo, http_fetch",
      memory_title:    "Agent memory",
      memory_empty:    "(empty memory)",
      memory_saved:    "Memory saved.",
      records_title:   "Records",
      records_desc:    "Agent activity log (messages/actions). Newest first.",
      sleep_title:     "Sleep / Heartbeat",
      sleep_desc:      "Agent execution status, derived from its routines.",
      sleep_deep:      "Deep sleep · no heartbeat",
      sleep_deep_desc: "This agent has no routine that triggers it. It does not run autonomously; it only responds when invoked (chat / task).",
      brain_title:     "Brain",
      brain_desc:      "Real relationship graph of the agent: memory, threads, tasks, heartbeats and hierarchy. (first version — will be refined)",
      brain_empty:     "No relationships to graph yet (no memory, threads, tasks or routines).",
      msgs_count:      "msgs",
    },

    mcps: {
      title:       "MCP servers",
      subtitle:    "3 scopes: runtime > shared > global. Conflicts shown above if any.",
      empty:       "No MCPs configured.",
      new:         "MCP",
      delete_confirm: "Delete MCP {name} from scope {scope}?",
      conflicts:   "⚠ Conflicts: {names}",
      conflict_detail: "{name} is defined in {winner} and {loser}. APX uses {winner}; {loser} is ignored.",
      new_title:   "New MCP",
      new_desc:    "POST /projects/:pid/mcps?scope=…",
      scope_label: "Scope",
      transport_label: "Transport",
      name_label:  "Name",
      name_ph:     "filesystem",
      cmd_label:   "Command",
      cmd_ph:      "npx",
      args_label:  "Args",
      args_hint:   "space-separated",
      args_ph:     "-y @modelcontextprotocol/server-filesystem /tmp",
      env_label:   "Env (JSON, optional)",
      url_label:   "URL",
      url_ph:      "https://example.com/mcp",
      enabled_label: "Enabled",
      add_btn:     "Add",
      name_required: "name required",
      env_invalid: "env must be valid JSON",
      removed:     "removed",
      added:       "MCP added.",
      updated:     "MCP updated.",
      edit_title:  "Edit MCP",
      save_btn:    "Save",
      add_arg:     "Add arg",
      edit_btn:    "Edit",
      test_btn:    "Test",
      logs_btn:    "Logs",
      testing:     "Testing…",
      test_ok:     "OK · {n} tools available",
      tools_count: "{n} tools",
      logs_title:  "Logs · {name}",
      logs_empty:  "No logs yet. Start the MCP by calling a tool or running Test.",
      logs_events: "Recent events",
      logs_stderr: "stderr (last 4KB)",
      logs_panel_title: "Live logs",
      logs_panel_pick:  "pick an MCP",
      logs_panel_hint:  "Click an MCP in the list to see what's happening live.",
      logs_panel_idle:  "No activity. Hit Test to spin it up.",
      scope_runtime: "Runtime",
      scope_shared:  "Shared",
      scope_global:  "Global",
      source_runtime: "Runtime",
      source_apc:     "APC / Shared",
      source_claude:  "Claude",
      source_codex:   "Codex",
      source_cursor:  "Cursor",
      source_vscode:  "VS Code",
      source_roo:     "Roo",
      source_gemini:  "Gemini",
      scope_runtime_desc: "This project only · with secrets · not committed (~/.apx/projects/<id>/mcps.json)",
      scope_shared_desc:  "This project only · committeable · no secrets (.apc/mcps.json)",
      scope_global_desc:  "All projects on this machine (~/.apx/mcps.json)",
      transport_stdio:    "stdio",
      transport_http:     "HTTP",
      transport_stdio_desc: "Local process — `command` + args",
      transport_http_desc:  "Remote endpoint — URL + headers",
      args_hint_tokens: "One entry per arg. Use the + button to insert a variable.",
      env_hint_tokens:  "Key/value pairs. Values accept ${var.NAME} (+ button on the right).",
      env_empty:        "No env vars.",
      headers_label:    "Headers",
      headers_hint:     "Key/value pairs — typically Authorization: Bearer ${var.TOKEN}.",
      headers_empty:    "No headers.",
    },

    vars: {
      title:                "Variables",
      subtitle_project:     "Replace ${var.NAME} when loading MCPs and templates. Project vars beat globals. Stored outside the repo (~/.apx/, chmod 0600).",
      subtitle_base:        "Global variables — available to every project. Stored in ~/.apx/vars.json (chmod 0600).",
      empty:                "No variables yet.",
      new:                  "Variable",
      new_title:            "New variable",
      edit_title:           "Edit variable",
      new_desc:             "Referenced as ${var.NAME} in any field that supports interpolation.",
      reveal_all:           "Show values",
      reveal:               "Show",
      hide:                 "Hide",
      filter_label:         "Show:",
      filter_all:           "All",
      filter_project:       "Project only",
      filter_global:        "Globals only",
      scope_label:          "Scope",
      scope_project:        "project",
      scope_project_desc:   "This project only. Beats the global with the same name.",
      scope_global:         "global",
      scope_global_desc:    "Available to every project.",
      name_label:           "Name",
      name_hint:            "Uppercase, digits and _. E.g. MY_API_KEY, GITHUB_TOKEN.",
      value_label:          "Value",
      value_hint:           "Stored on disk with 0600 perms. Never committed.",
      value_edit_ph:        "(leave empty to keep current… not yet supported, paste the value again)",
      add_btn:              "Add",
      save_btn:             "Save",
      edit_btn:             "Edit",
      delete_btn:           "Delete",
      delete_confirm:       "Delete {name} ({scope})?",
      removed:              "Variable removed.",
      added:                "Variable added.",
      updated:              "Variable updated.",
      name_required:        "Name required.",
      value_required:       "Value required.",
    },

    threads: {
      title:       "Chats",
      subtitle:    "Conversations per agent (empty = no logs persisted yet).",
      no_agents:   "No agents. Conversations require a configured agent.",
      pick:        "Pick an agent to view its conversations.",
      empty:       "No conversations for {slug}.",
      conversation_title: "Conversation {id}",
      messages:    "messages",
      via:         "via",
    },

    config: {
      title:       "Quick config",
      subtitle:    "Project override. Written to {path}.",
      model:       "super_agent.model",
      model_hint:  "e.g. anthropic:claude-sonnet-4.5, ollama:gemma2:9b",
      perm:        "super_agent.permission_mode",
      route:       "route_to_agent",
      route_hint:  "Slug of the agent that handles this project by default.",
      use_global:  "(uses global)",
      saved:       "Saved.",
      nothing:     "Nothing to save.",
      raw_title:   "Config (raw JSON)",
      raw_subtitle:"Paste the entire object — equivalent to PUT the file.",
      raw_save:    "Replace config",
      raw_done:    "Config overwritten.",
      effective:   "Effective config (read-only)",
      effective_sub: "What the daemon actually sees (global ⊕ override).",
      section_title: "Project config",
      section_desc: "APC metadata and overrides separated. General APX lives in Settings > Config.",
      effective_read: "Read: global APX + project override.",
      save_project: ".apc/project.json saved.",
      save_override: ".apc/config.json saved.",
      save_fields_success: "Overrides saved.",
      save_meta_success: "Project metadata saved.",
      no_data:     "No data.",
      tab_settings: "Settings",
      tab_project:  "Project",
    },

    telegram: {
      title:        "Telegram channel (override)",
      subtitle:     "If you set a channel here, messages from this project go there instead of the default.",
      use_default:  "Use default channel",
      bot_token:    "Bot token (override)",
      chat_id:      "Chat ID (override)",
      saved:        "Override saved.",
      cleared:      "Override removed — falls back to default.",
      override_active: "override active",
      channel_badge: "Channel {name}",
      no_override:  "No override. Messages from this project go to the default channel.",
      respond_engine: "Respond with engine",
      route_agent:  "route_to_agent",
      route_hint:   "Slug of the agent that handles messages (empty = super-agent).",
      bot_hint_none: "If empty, inherits from default.",
    },

    memories: {

      super_agent_group: "Super-agent",

      notebook_item: "{persona}'s notebook",

      tokens: "~{n} tok",
      sidebar_title:   "Memories",
      general_group:   "General",
      general_item:    "Project memory",
      project_title:   "Project memory",
      project_desc:    "Durable facts at the project level. .apc/memory.md — read by agents and the super-agent.",
      project_ph:      "# Project Memory\n\nStable facts that any agent should know…",
      agents_title:    "Agent memories",
      agents_desc:     "Individual memory per agent. ~/.apx/projects/<id>/agents/<slug>/memory.md",
      no_agents:       "No agents in this project.",
      saved:           "Memory saved.",
      empty:           "(empty memory)",
      chars:           "chars · Markdown",
      save_btn:        "Save",
    },
  },

  base: {
    title:              "Base",
    subtitle:           "General workspace · super-agent",
    nav_general:        "General",
    nav_activity:       "Activity",
    nav_system:         "System",
    workspaces_title:   "Workspaces",
    workspaces_desc:    "All projects registered in APX.",
    workspaces_new:     "New project",
    workspaces_empty:   "No projects. Add one with the button above.",
    sessions_title:     "Sessions",
    sessions_desc:      "Sessions from all engines (apx · claude · codex), newest first.",
    sessions_desc_scoped: "Sessions in this project's folder ({path}), all engines, newest first.",
    sessions_all:       "All engines",
    sessions_empty:     "No sessions.",
    sessions_error:     "Could not read sessions: {msg}",
    sessions_search_ph: "Search sessions…",
    sessions_deep:      "Deep",
    sessions_deep_tip:  "Also search inside transcripts (slower)",
    sessions_clear:     "Clear filters",
    sessions_refresh:   "Refresh list",
    sessions_no_match:  "No sessions match “{q}”.",
    sessions_act_cmd:   "Copy apx command",
    sessions_act_ask:   "Ask {name} to continue",
    sessions_act_folder:"Open folder",
    sessions_act_path:  "Copy path",
    sessions_cmd_copied:"Command copied — paste it in your terminal",
    sessions_path_copied:"Path copied",
    sessions_copy_failed:"Could not copy",
    sessions_no_folder: "This session has no folder",
    sessions_no_path:   "This session has no path",
    sessions_folder_failed:"Could not open folder: {msg}",
    defaults_title:     "Agent defaults",
    defaults_desc:      "Global vault templates. Bundled ones come with APX and are always present; ones you create or edit go in ~/.apx/agents and override. Import them into a project from Agents › Import.",
    defaults_show_removed: "Show removed",
    defaults_new:       "New",
    defaults_empty:     "No templates in the vault.",
    defaults_hide:      "Hide",
    defaults_restore:   "Restore",
    defaults_edit:      "Edit",
    defaults_remove:    "Hide",
    defaults_delete:    "Delete",
    defaults_tombstone_msg: "Hide the default \"{slug}\"? It's bundled — tombstoned and recoverable with Restore.",
    defaults_delete_msg: "Delete the template \"{slug}\"?",
    defaults_hidden:    "Hidden.",
    defaults_deleted:   "Deleted.",
    defaults_restored:  "Restored.",
    defaults_new_title: "New template",
    defaults_new_desc:  "POST /agents/vault — saved to ~/.apx/agents/<slug>.md",
    defaults_edit_title: "Edit \"{slug}\"",
    defaults_bundled_desc: "This is a bundled default. Saving does a copy-on-write to ~/.apx/agents/<slug>.md (becomes an override).",
    defaults_user_desc:  "PATCH /agents/vault/:slug — edits the file in ~/.apx/agents.",
    defaults_master_label: "Master agent",
    defaults_slug_invalid: "invalid slug (must match /^[a-z][a-z0-9_-]*$/)",
    defaults_created:   "Template \"{slug}\" created.",
    defaults_saved:     "Template \"{slug}\" saved.",
  },

  logs: {
    title:        "Logs",
    desc_global:  "Daemon activity (global channels: telegram, direct…). ~/.apx/messages/<channel>/.",
    desc_project: "Project activity. ~/.apx/projects/<id>/messages/.",
    filter_channel: "filter channel (e.g. telegram)",
    filter_dir:   "direction",
    all_directions: "All directions",
    in:           "Incoming (in)",
    out:          "Outgoing (out)",
    filter_type:  "type",
    all_types:    "All types",
    search_text:  "search text…",
    count_of:     "of",
    no_activity:  "No activity.",
    no_activity_ch: "No activity in channel \"{ch}\".",
    error:        "Could not read messages: {msg}",
    show_more:    "show more",
    event_routine_created: "routine created",
    event_routine_updated: "routine updated",
    show_less:    "show less",
    daemon_errors: "Daemon errors (~/.apx/logs/errors.jsonl)",
    no_errors:    "No errors recorded. 🎉",
  },

  telegram_contacts: {
    title:       "Telegram contacts",
    desc:        "Who writes to the bots. The role defines which tools they can use; a guest has no permissions until you assign a role.",
    empty:       "No contacts yet — they register automatically when someone writes to a bot.",
    owner_badge: "owner",
    assign_role: "Assign role",
    owner_hint:  "Channel owner — change it from the channel",
    removed:     "Contact deleted.",
    delete_confirm: "Delete contact {name}?",
    last_seen:   "seen:",
    tools_all:   "tools: all",
    tools_none:  "tools: none",
    tools_label: "tools:",
  },

  telegram_channels: {
    title:       "Channels",
    desc:        "Each channel is a bot the daemon polls. Here you can add/remove channels, change the answering agent, the project it belongs to and its owner.",
    new_btn:     "New channel",
    empty:       "No channels yet — add the first one.",
    removed:     "Channel deleted.",
    delete_confirm: "Delete channel {name}?",
    no_owner:    "no owner (claimed on first DM)",
    owner_label: "owner:",
  },

  telegram_channel_dialog: {
    new_title:   "New Telegram channel",
    edit_title:  "Edit channel: {name}",
    name_label:  "name (internal slug)",
    token_label: "bot_token",
    chat_id:     "chat_id",
    project_label: "project",
    project_hint: "Slug or id of the project to pin this channel to (optional).",
    route_label: "route_to_agent",
    route_hint:  "Answering agent; empty = APX super-agent.",
    owner_label: "owner_user_id",
    owner_hint:  "Telegram user_id of the channel owner. Overrides global role to 'owner' here. Leave empty — first private message claims it.",
    owner_ph:    "889721252",
    respond_label: "Respond with engine (not echo)",
    name_required: "name required",
    saved:       "Channel saved.",
  },

  telegram_send_dialog: {
    title:       "Send to {name}",
    default_msg: "Test message from APX panel ✅",
  },

  telegram_roles: {
    title:       "Roles",
    desc:        "Each role defines which super-agent tools the assigned user can invoke. 'owner' always = all; 'guest' always = none (chat only).",
    empty:       "No roles defined.",
    tools_all:   "all tools",
    tools_none:  "no tools",
    builtin:     "built-in",
    delete_confirm: "Delete role \"{name}\"?",
    removed:     "Role deleted.",
    saved:       "Role \"{name}\" saved.",
    name_required: "Name required.",
    builtin_error: "\"{name}\" is a built-in role.",
    new_title:   "New role or replace a custom one",
    name_label:  "Name",
    name_ph:     "editor",
    tools_label: "Tools (comma-separated)",
    tools_hint:  "Empty = none. Examples: call_agent, list_tasks, create_task.",
    tools_ph:    "call_agent, list_tasks",
    full_access: "Full access (all tools)",
    save_btn:    "Save role",
    delete_btn:  "Delete",
  },

  superagent: {
    title:          "{persona}",
    badge:          "super-agent · APX",
    desc:           "Quick chat with your super-agent. Has access to tools (projects, tasks, mcps, agents); for a longer persistent thread, open Chats.",
    empty:          "Send {persona} a message to get started.",
    thinking:       "{persona} is thinking…",
    talk:           "Talk to {persona}",
    new_chat:       "New chat",
    placeholder:    "Type and press enter to send (shift+enter = new line)…",
  },

  not_found: {
    title:   "404",
    message: "Roby got lost: this page doesn't exist or has moved.",
    home:    "Back to home",
  },

  ask_panel: {
    answers_header: "Answers",
    other:          "Other",
    other_placeholder: "Write your own answer here",
    text_placeholder:  "Type your answer…",
    back:           "Back",
    skip:           "Skip",
    next:           "Next",
    submit:         "Send",
    status_waiting: "Waiting for your answer…",
    status_received: "Answers received",
  },
  code_module: {
    title:        "Code",
    badge:        "super-agent",
    desc:         "OpenCode-style coding sessions. Pick a project, open a session, and ask it to read, plan, edit or run.",
    no_projects:  "No registered projects. Register one with `apx project add` to use Code.",
    sessions:     "Sessions",
    new_session:  "New session",
    untitled:     "New session",
    no_sessions:  "No sessions yet — create one to start coding.",
    pick_project: "Pick a project to see its sessions.",
    rename:       "Rename",
    delete:       "Delete",
    delete_confirm: "Delete this session? The transcript is removed; your files are untouched.",
    empty_chat:   "Send a coding instruction to get started.",
    placeholder:  "Ask for a change… (enter sends, shift+enter = new line)",
    mode_build:   "Build",
    mode_plan:    "Plan",
    mode_build_hint: "Build — edits files and runs commands",
    mode_plan_hint:  "Plan — read-only, proposes changes without touching files",
    tab_context:  "Context",
    tab_changes:  "Changes",
    tab_artifacts: "Artifacts",
    artifacts_none: "No artifacts yet. Ask the agent to create a script under `artifacts/<name>`.",
    artifacts_count: "{n} artifact(s)",
    artifacts_copy_path: "Copy path",
    artifacts_run: "Run",
    artifacts_run_hint: "Run it from your terminal:",
    artifacts_delete: "Delete",
    artifacts_delete_confirm: "Delete this artifact? The file will be removed from disk.",
    ctx_model:    "Model",
    ctx_tokens:   "Tokens",
    ctx_input:    "Input",
    ctx_output:   "Output",
    ctx_messages: "Messages",
    ctx_breakdown: "Context breakdown",
    ctx_none:     "No usage yet — send a turn to see tokens.",
    seg_system:   "System",
    seg_user:     "User",
    seg_assistant: "Assistant",
    seg_tool:     "Tools",
    seg_other:    "Other",
    changes_none: "No changes in this session yet.",
    changes_no_git: "Changes need a git repository. This project isn't one.",
    changes_files: "{n} file(s) changed",
    stopped:      "[stopped]",
    close:               "Close",
    reload:              "Reload",
    discard_changes:     "Discard changes",
    save_shortcut_hint:  "Save (Cmd/Ctrl+S)",
    artifacts_rename:    "Rename",
    artifacts_view:      "View contents",
    artifacts_edit:      "Edit contents",
    artifacts_preview:       "Preview",
    artifacts_preview_hint:  "Open a live preview in a local browser tab",
    artifacts_share:         "Share",
    artifacts_share_hint:    "Create a public tunnel URL to share this preview",
    artifacts_stop_preview:  "Stop preview",
    artifacts_preview_local: "Local preview",
    artifacts_preview_public:"Public URL",
    artifacts_copy_url:      "Copy URL",
    artifacts_preview_started: "Preview running at {url}",
    tree_collapse_all:   "Collapse all",
    terminal_clear:      "Clear",
    terminal_close:      "Close terminal",
  },

  desktop_screen: {
    status_title:        "Status",
    autostart_title:     "Auto-start",
    shortcut_title:      "Keyboard shortcut",
    appearance_title:    "Appearance",
    activation_title:    "Activation + transcription",
    last_conv_title:     "Last conversation",
    open_config:         "Configuration",
  },

  voice_screen: {
    providers_title:     "Voice providers (TTS)",
    test_title:          "Test voice",
    stt_title:           "Transcription (STT)",
    configure_provider:  "Configure {name}",
  },

  deck_screen: {
    widgets_title:       "Widgets",
    context_title:       "APX context",
    reload_manifest:     "Reload manifest",
    widget_native:       "Native APX widget",
    widget_external:     "External widget",
    preview_badge:       "Preview",
    preview_title:       "Deck — Coming soon",
    preview_body:        "The Deck module is still in development and not released yet. We'll re-enable it once Deck ships in a stable release. For now everything here is read-only and no changes will be saved.",
  },

  memory_panel: {
    embeddings_title:    "Embeddings (RAG)",
    embeddings_desc:     "Model that vectorizes the history of all channels for relevant memory. Just like TTS/STT: pick a provider and model. 'Automatic' tries local first and falls back to offline if nothing is available.",
    provider_label:      "Provider",
    provider_hint:       "Ollama is local and free. Gemini/OpenAI use the API key from their section in Models (or the one below).",
    mode_label:          "Selection mode",
    mode_hint:           "Chain falls back to the next if one fails; Single uses exactly the chosen provider.",
    available:           "available",
    unavailable:         "unavail.",
    test_btn:            "Test embedding",
    reindex_btn:         "Reindex memory",
    test_ok:             "Embedding OK with {embedder}",
    test_failed:         "Test failed: {msg}",
    reindexed:           "Reindexed: {indexed} chunks (cleared {cleared}).",
    reindex_failed:      "Reindex failed: {msg}",
    save_failed:         "Could not save: {msg}",
    provider_auto:       "Automatic (chain: Ollama → Gemini → OpenAI → offline)",
    provider_ollama:     "Ollama — local, no API key (nomic-embed-text)",
    provider_gemini:     "Gemini — free tier with key (text-embedding-004)",
    provider_openai:     "OpenAI — text-embedding-3-small (cloud)",
    provider_tf:         "Offline (term-frequency, no model — degraded)",
    mode_chain:          "Chain (automatic fallback)",
    mode_single:         "Single (uses only the chosen one)",
    ollama_title:        "Ollama (local)",
    ollama_desc:         "No API key. Runs nomic-embed-text on your local or cloud Ollama.",
    model_label:         "Model",
    base_url_label:      "Base URL",
    ollama_base_url_hint:"Empty uses engines.ollama.base_url (default http://localhost:11434).",
    openai_title:        "OpenAI",
    openai_desc:         "text-embedding-3-small (1536 dims) or another compatible model.",
    api_key_label:       "API key",
    openai_key_hint:     "Empty reuses engines.openai.api_key. Leave it blank to keep the saved one.",
    gemini_title:        "Gemini",
    gemini_desc:         "text-embedding-004 (768 dims). Free tier with a Google API key.",
    gemini_key_hint:     "Empty reuses engines.gemini.api_key.",
    compaction_title:    "History compaction",
    compaction_desc:     "When a chat exceeds the turn threshold, the oldest turns are summarized with a lightweight (local) LLM and saved as [COMPACTED SUMMARY], keeping context bounded. Runs off the hot-path: the current turn uses whatever summary already exists.",
    threshold_label:     "Compaction threshold",
    threshold_hint:      "Compact once the chat exceeds these turns (default 60).",
    keep_recent_label:   "Recent turns to preserve",
    keep_recent_hint:    "Verbatim turns that are NEVER compacted (default 40). Must be lower than the threshold.",
    compact_model_label: "Compaction model",
    compact_model_hint:  "Lightweight LLM for summarizing. Ideally a local one (Ollama) to avoid cost. Format provider:model.",
    compact_fallback_label: "Fallback model",
    compact_fallback_hint:  "Used if the compaction one fails. Empty falls back to the super-agent model.",
    compact_fallback_ph: "(empty → super-agent model)",
  },

  router_panel: {
    title:               "Model router",
    description:         "A single general router (no per-task cases). Pick a provider and model; if the active one fails, it tries the fallback chain in order.",
    badge_default:       "default",
    no_providers:        "Add a provider below to be able to pick models.",
    active_model_label:  "Active model (default)",
    active_model_hint:   "Provider + model. Saved as provider:model.",
    fallback_title:      "Fallback chain",
    fallback_desc:       "If the active model fails, it tries these in order. Click one to edit it.",
    fallback_empty:      "No fallback configured.",
    add_to_chain:        "Add to the chain",
    done:                "done",
    save:                "Save router",
    saved:               "Saved",
    saved_toast:         "Router saved.",
    provider_ph:         "— provider —",
    provider_not_found:  "⚠ {name} (not found)",
    provider_not_configured: "The provider \"{name}\" is not configured.",
  },

  routing_panel: {
    title:            "Content routing",
    description:      "Prefer a different model per message based on its content (image, size, channel, keywords). Separate from the fallback chain above.",
    signal_on:        "Content routing: ON ({n} rules)",
    signal_on_empty:  "Content routing: ON (no rules yet)",
    signal_off:       "Content routing: OFF",
    how_it_works:     "How does it work?",
    enable_label:     "Enable content routing",
    rules_title:      "Routing rules",
    rules_desc:       "Evaluated top to bottom; the first rule whose conditions all match wins.",
    rules_empty:      "No rules yet. Add some in the editor.",
    edit_rules:       "Edit rules (JSON)",
    hide_editor:      "Hide editor",
    editor_label:     "Rules (JSON array)",
    json_hint:        "Array of { model, when }. when keys: has_image, min_prompt_chars, max_prompt_chars, min_context_chars, channels[], keywords[]. Empty when = matches every message.",
    json_error:       "Invalid JSON: {msg}",
    json_not_array:   "The rules must be a JSON array.",
    insert_example:   "Insert an example",
    when_any:         "any message",
    when_image:       "has image",
    when_no_image:    "no image",
    when_min_prompt:  "prompt ≥ {n} chars",
    when_max_prompt:  "prompt ≤ {n} chars",
    when_min_context: "context ≥ {n} chars",
    when_channels:    "channels: {list}",
    when_keywords:    "keywords: {list}",
    helper:           "Routing picks a model per message (image, size, channel, keywords). It composes with failover: a routed model that is down falls back down the chain. An explicit per-request model override always wins.",
    save:             "Save routing",
    saved:            "Saved",
    saved_toast:      "Content routing saved.",
    confirm_title:    "Apply routing changes?",
    confirm_body:     "This changes which model handles each message. Failover still applies if a routed model is down.",
    confirm_on:       "Content routing will be ON with {n} rules.",
    confirm_off:      "Content routing will be OFF (every message uses the default router).",
    confirm_apply:    "Apply",
    cancel:           "Cancel",
  },

  engines_panel: {
    title:               "Providers",
    new_btn:             "New provider",
    description:         "LLM providers (API). Each provider uses an engine/adapter (openai, ollama, …) with its key and URL.",
    empty:               "No providers. Add one with the button above.",
    add_card:            "Add provider",
    saved:               "Provider saved.",
    saved_json:          "Provider saved (JSON).",
    deleted:             "Provider deleted.",
    delete_confirm:      "Delete provider {name}?",
  },

  providers_modal: {
    new_title:           "New provider",
    edit_title:          "Edit {name}",
    description:         "LLM provider. The engine defines which adapter it uses (openai, ollama, …).",
    list_models_hint:    "List the provider's actual models",
    toggle_active:       "Active · click to deactivate",
    toggle_inactive:     "Inactive · click to activate",
    delete:              "Delete",
    custom:              "Custom",
    json_mode:           "JSON",
    form_mode:           "Back to form",
    json_label:          "Provider config (JSON)",
    json_hint:           "Saved as engines.{slug} in config.json",
    json_help:           "Must be a valid JSON object with at least engine. The slug is taken from the form.",
    name_label:          "Name",
    name_ph:             "My provider",
    engine_label:        "Engine",
    base_url_label:      "Base URL (base_url)",
    base_url_hint:       "Auto-filled when you pick a provider.",
    base_url_ph:         "https://api.openai.com/v1",
    api_key_label:       "API key",
    api_key_hint_existing: "Leave blank to keep the current one.",
    api_key_hint_env:    "Stored as a secret. Suggested env: {env}",
    api_key_hint:        "Stored as a secret.",
    api_key_set:         "…{suffix} (already set)",
    model_label:         "Default model",
    load_models:         "Load models",
    max_tokens_label:    "Max tokens (max_tokens)",
    temperature_label:   "Temperature: {value}",
    pricing_summary:     "Token analysis / pricing (optional)",
    context_limit_label: "Context limit (tokens)",
    price_input:         "$ input / 1M",
    price_output:        "$ output / 1M",
    price_cache_read:    "$ cache read / 1M",
    price_cache_write:   "$ cache write / 1M",
    model_limits_label:  "Per-model context limits (JSON)",
    active_label:        "Active (agents can use it)",
    err_slug_required:   "Slug required.",
    err_slug_required_form: "Slug required (in the form).",
    err_slug_exists:     "A provider \"{slug}\" already exists.",
    err_model_limits_json: "Per-model context limits: invalid JSON.",
    err_json_invalid:    "Invalid JSON: check the syntax.",
    err_json_object:     "The JSON must be an object with the provider config.",
    err_engine_missing:  "Missing \"engine\" (e.g. \"anthropic\", \"ollama\").",
    err_save:            "Error saving.",
    err_no_models:       "No models. Correct key/URL?",
    err_list_models:     "Could not list models.",
  },

  providers_card: {
    active:              "Active",
    off:                 "Off",
    model:               "Model",
    base_url:            "Base URL",
    api_key:             "API key",
    key_set:             "✓ set",
    temp:                "Temp",
    price_io:            "$ in/out (1M)",
  },

  chat_ui: {
    copy:                "Copy",
    stop:                "Stop",
    send:                "Send",
    pick_model:          "Pick model (or Auto)",
    insert_variable:     "Insert variable",
    ctx_files:           "files",
    ctx_actors:          "{n} agents/models",
    ctx_turns:           "{n} turns",
  },

  sidebar_ui: {
    toggle:              "Toggle sidebar",
  },

  models_ui: {
    invalid_hint:        "Model/provider unavailable",
  },

  global_config: {
    title:               "APX config",
  },

  agent_detail_extra: {
    skills_title:        "Skills & tools",
  },

  voice_ui: {
    api_key_label:        "API key",
    api_key_set:          "…{suffix} (already set)",
    api_key_keep_hint:    "Leave blank to keep the current one.",
    api_key_secret_hint:  "Stored as a secret. Env: {env}",
    api_key_reuse_hint:   "Reuses {engine} if left blank. Env: {env}",
    err_save:             "Error while saving.",
    model_label:          "Model",
    voice_label:          "Voice",
    format_label:         "Format",
    output_format_label:  "Output format",
    voice_id_label:       "Voice ID",
    voice_id_hint:        "ElevenLabs voice id (empty = default).",
    gemini_model_hint:    "Gemini TTS is still in preview.",
    base_url_label:       "Base URL (optional)",
    base_url_hint:        "OpenAI-compatible endpoint. Empty = OpenAI. Point it at a local server (e.g. a QVox / Qwen3-TTS daemon) to use that instead.",
    openai_model_hint:    "tts-1 / tts-1-hd for OpenAI. Leave blank to let a custom server pick.",
    openai_voice_hint:    "OpenAI preset (alloy…) or a custom server's preset (e.g. custom). Empty = server default.",
    openai_style_hint:    "Base voice / instruct, used by custom endpoints (the persona kept across the audio). Ignored by stock OpenAI tts-1.",
    style_label:          "Style (how it should speak)",
    style_hint:           "Natural-language instruction. Empty = no style. E.g.: 'speak in a cheerful, unhurried tone'.",
    style_ph:             "speak in a cheerful, energetic tone",
    temperature_label:    "Temperature (optional)",
    temperature_hint:     "Sampling temperature for custom endpoints. Empty = server default.",
    emotions_short:       "Emotions",
    emotions_label:       "Inline emotion tags",
    emotions_hint:        "When this engine speaks, let the agent drop [happy]/[whisper]-style tags into voice replies to color the delivery. Only enable it if this engine understands the tags (e.g. a QVox/Qwen3-TTS endpoint) — otherwise they're stripped before synthesis.",
    emotions_tags_label:  "Allowed tags",
    emotions_tags_hint:   "Comma-separated. Empty = the default set.",
    piper_bin_label:      "Binary (bin)",
    piper_bin_hint:       "Path or name of the piper CLI (PATH).",
    piper_model_label:    "Model (.onnx)",
    piper_model_hint:     "Absolute path to the piper voice model.",
    piper_speaker_label:  "Speaker (optional)",
    piper_speaker_hint:   "Speaker id for multi-voice models.",
    mock_desc:            "The mock engine generates a silent test WAV. It has no parameters: it serves as a guaranteed fallback when no other engine is configured.",
    selection_mode:       "Selection mode",
    mode_chain_desc:      "Chain with fallback: uses the first available engine following the order below.",
    mode_single_desc:     "Default engine only: always uses the chosen one; the rest stay configured for other purposes.",
    mode_chain_btn:       "Chain (router)",
    mode_single_btn:      "Default engine only",
    move_up:              "Move up",
    move_down:            "Move down",
    badge_local:          "local",
    badge_available:      "available",
    badge_unavailable:    "configured, unavailable",
    badge_not_configured: "not configured",
    badge_default:        "default",
    badge_custom:         "custom",
    set_as_default:       "Set as default",
    configure:            "Configure",
    remove:               "Remove",
    remove_confirm:       "Remove this custom provider?",
    add_provider:         "Add provider",
    new_provider:         "New provider",
    custom_note:          "Custom OpenAI-compatible endpoint.",
    custom_desc:          "Any OpenAI-compatible speech endpoint (e.g. a local QVox / Qwen3-TTS server).",
    label_label:          "Name",
    label_hint:           "Display name for this provider.",
    base_url_req_label:   "Base URL",
    base_url_req_hint:    "Required. The OpenAI-compatible endpoint, e.g. http://127.0.0.1:5111/v1",
    api_key_optional_hint:"Optional — only if your server requires a key.",
    advanced:             "Advanced",
    custom_model_hint:    "Optional. Most local servers ignore it (e.g. QVox).",
    custom_voice_hint:    "Optional. A preset your server understands (e.g. custom). Empty = server default.",
    custom_optional_ph:   "(optional)",
    stt_engine_label:     "Transcription engine",
    stt_engine_hint:      "Local uses faster-whisper (requires python3 + faster-whisper). OpenAI uses the engines.openai key.",
    stt_model_label:      "Local model (whisper)",
    stt_model_hint:       "Bigger = more accurate and slower.",
    stt_language_label:   "Language",
    stt_language_hint:    "For Spanish, setting \"Spanish\" improves accuracy.",
    stt_provider_auto:    "Automatic (local, then remote)",
    stt_provider_local:   "Local — faster-whisper (offline)",
    stt_provider_openai:  "OpenAI — Whisper-1 (cloud)",
    stt_provider_custom:  "Custom — OpenAI-compatible server",
    stt_openai_model_label: "OpenAI model",
    stt_openai_model_hint:  "Defaults to whisper-1.",
    stt_custom_baseurl_label: "Base URL (OpenAI-compatible)",
    stt_custom_baseurl_hint:  "e.g. http://localhost:8000/v1 (mlx-audio on Metal) or http://192.168.1.50:9000/v1 (Radeon/NVIDIA on the LAN).",
    stt_custom_model_label:   "Model",
    stt_custom_model_hint:    "e.g. mlx-community/whisper-large-v3-turbo or large-v3.",
    stt_custom_key_hint:      "Optional — most local servers need no key.",
    stt_hw_label:             "Detected hardware",
    stt_hw_recommended:       "Recommended",
    stt_hw_limited:           "limited GPU acceleration, using CPU",
    stt_backend_label:        "Acceleration / Engine",
    stt_backend_hint:         "Auto adapts to your hardware. Metal runs on the GPU (mlx); CPU uses faster-whisper.",
    stt_backend_auto:         "Automatic (recommended)",
    stt_model_needs_download: "Not downloaded (~{size}). The model must be downloaded to use this engine.",
    lang_auto:            "Auto-detect",
    lang_es:              "Spanish",
    lang_en:              "English",
    lang_pt:              "Portuguese",
    lang_fr:              "French",
    lang_it:              "Italian",
    lang_de:              "German",
    test_default_text:    "Hi, I'm APX. This is a voice test.",
    test_default_engine:  "Default ({name})",
    test_default_chain:   "Default (chain)",
    test_unavailable_suffix: " · unavailable",
    test_empty_error:     "Type something to say.",
    test_synth_error:     "Could not synthesize.",
    test_engine_label:    "Engine",
    test_engine_hint:     "Override the default for testing.",
    test_style_label:     "Style (Gemini only)",
    test_style_hint:      "How it should speak. Empty = no style.",
    test_text_label:      "Text to say",
    test_text_ph:         "Type what you want it to say…",
    say_this:             "Say this",
    stop:                 "Stop",
    replay:               "Replay",
    engine_result:        "Engine",
    providers_desc:       "Synthesis engines, in fallback order. Status is reported live by the daemon. Add your own OpenAI-compatible endpoints.",
    providers_load_error: "Could not load providers: {msg}",
    test_desc:            "Pick which engine to synthesize with and, if applicable, how it should speak.",
    stt_desc:             "Speech-to-text engine used by the deck, Telegram, and the CLI when listening.",
    toast_default_engine: "Default engine: {id}.",
    toast_mode_chain:     "Mode: chain with fallback.",
    toast_mode_single:    "Mode: default engine only.",
    toast_config_saved:   "Voice configuration saved.",
    toast_provider_removed: "Provider removed.",
    err_label_required:   "A name is required.",
    err_base_url_required:"A base URL is required.",
    toast_transcription_updated: "Transcription updated.",
  },

  telegram_ui: {
    channel_dialog_desc:   "POST /telegram/channels (upsert) — PATCH /telegram/channels/:name (partial).",
    bot_token_hint:        "BotFather token. Stored in ~/.apx/config.json.",
    bot_token_hint_short:  "BotFather token.",
    secret_set_replace:    "(set — type to replace)",
    secret_already_set:    "(already set)",
    empty_keep:            "— empty = keep",
    message_sent:          "Message sent.",
    message_label:         "Text",
    send_chat_id:          "chat_id: {id}",
    default_apx:           "default APX",
    yes:                   "yes",
    no:                    "no",
    user_id_fallback:      "user_id {id}",
    role_assigned:         "{name} → {role}",
  },

  agents_form: {
    emoji:           "Emoji",
    area:            "Area",
    role:            "Role",
    no_role:         "— no role —",
    autonomy:        "Autonomy",
    autonomy_hint:   "How much the agent can do without asking for confirmation.",
    auto_total:      "Total",
    auto_automatico: "Auto",
    auto_permiso:    "Permission",
  },

  structure: {
    title:            "Structure",
    subtitle:         "Company areas and roles. Areas group agents; roles define their function.",
    info:             "Areas are optional groupings. Roles define an agent's function and may belong to an area.",
    empty:            "No areas or roles yet. Create the first one above.",
    new_area:         "New area",
    new_role:         "New role",
    edit_area:        "Edit area",
    edit_role:        "Edit role",
    create_area:      "Create area",
    create_role:      "Create role",
    name:             "Name",
    slug:             "Slug",
    goal:             "Goal",
    goal_hint:        "What this area exists for (optional).",
    area:             "Area",
    description:      "Description",
    no_area:          "— no area —",
    roles:            "Roles",
    add_role:         "role",
    no_roles:         "no roles",
    general_roles:    "General roles",
    delete_area:      "Delete area",
    delete_role:      "Delete role",
    delete_area_desc: "Delete area \"{name}\"? Its roles are detached, not deleted.",
    delete_role_desc: "Delete role \"{name}\"?",
  },

  files: {
    docs_label:    "Docs",
    files_label:   "Files",
    new_doc:       "New document",
    new_doc_hint:  "Folders allowed: cases/onboarding/spec.md",
    empty:         "No files.",
    docs_empty:    "No documentation yet. Create the first document.",
    truncated:     "Listing truncated (too many files).",
    select_prompt: "Pick a file to view it.",
    save:          "Save",
    saved:         "Saved.",
    deleted:       "Deleted.",
    created:       "Document created.",
    edit:          "Edit",
    preview:       "Preview",
    discard:       "Discard",
    no_preview:    "No preview for this file.",
    too_large:     "File too large to display.",
    path_label:    "File path",
    path_example:  "e.g. cases/onboarding/spec.md",
    create:        "Create",
  },

  tasks: {
    state_open:      "open",
    state_done:      "done",
    state_dropped:   "dropped",
    status_pending:   "pending",
    status_running:   "running",
    status_in_review: "in review",
    status_blocked:   "blocked",
    done_label:      "done",
    dropped_label:   "dropped",
    detail_title:    "Task detail",
    field_title:     "Title",
    field_prompt:    "Prompt",
    field_status:    "Status",
    field_agent:     "Agent",
    field_creator:   "Created by",
    field_source:    "Source",
    field_created:   "Created",
    field_updated:   "Updated",
    field_done:      "Completed",
    prompt_ph:       "Task description / prompt…",
    toggle_prompt:   "Prompt",
    view_thread:     "View thread",
    mark_done:       "Complete",
  },

  agents_ui: {
    model_router_default:    "model: router default",
    slug_kebab_hint:         "kebab-case, e.g. reviewer, my-agent, content-writer",
    comma_separated:         "comma-separated",
    body_hint:               "markdown — extends the agent's system prompt",
    source_user:             "user",
    source_override:         "override",
    source_bundled:          "bundled",
    tab_explorer:            "Explorer",
    type_none:               "— no type —",
    type_orchestrator:       "Orchestrator",
    type_orchestrator_desc:  "Coordinates the team and delegates.",
    type_specialist:         "Specialist",
    type_specialist_desc:    "Domain expert; runs tasks.",
    type_assistant:          "Assistant",
    type_assistant_desc:     "Conversational helper.",
    type_worker:             "Worker",
    type_worker_desc:        "Runs autonomous tasks.",
    type_monitor:            "Monitor",
    type_monitor_desc:       "Watches state and reports.",
    stat_threads:            "Threads",
    stat_records:            "Records",
    stat_tasks:              "Tasks",
    stat_heartbeats:         "Heartbeats",
    uncategorized:           "Uncategorized",
    brain_zoom_in:           "Zoom in",
    brain_zoom_out:          "Zoom out",
    brain_fit:               "Fit to view",
    brain_fullscreen:        "Fullscreen",
    brain_exit_fs:           "Exit fullscreen",
    brain_pan_hint:          "scroll to zoom · drag background to pan",
    brain_expand:            "Expand brains",
    brain_collapse:          "Collapse",
    brain_open:              "Open",
    brain_part_of:           "Part of",
    brain_branches:          "Branches",
    config_def_desc:         "definition (frontmatter + system prompt).",
    memory_durable_desc:     "durable facts the agent remembers.",
    running:                 "running",
    paused:                  "paused",
    last_error:              "last: error",
    field_tick:              "Tick",
    field_next_tick:         "Next tick",
    field_last_tick:         "Last tick",
    field_last_run:          "Last run",
    tools_label:             "Tools",
    kind_agent:              "agent",
    kind_memory:             "memory",
    kind_thread:             "thread",
    kind_task:               "task",
    kind_routine:            "routine",
    kind_hierarchy:          "hierarchy",
    nodes_drag_hint:         "{n} nodes · drag to rearrange",
    kind_watch:              "Watcher",
    kind_watch_desc:         "Sweeps for things worth noticing — overdue work, promises coming due, projects gone quiet. Costs nothing when there is nothing to report.",
    action_watch:            "Watches for signals, and judges only when it finds some",
    kind_exec_agent:         "Project agent",
    kind_exec_agent_desc:    "Runs a project agent with a prompt. You pick which one.",
    kind_super_agent:        "Super-agent",
    kind_super_agent_desc:   "Calls the APX super-agent with a prompt.",
    kind_telegram:           "Telegram",
    kind_telegram_desc:      "Sends a fixed message to a Telegram channel. No model or agent.",
    kind_shell:              "Shell",
    kind_shell_desc:         "Runs a shell command. No prompt or pre/post — the command is the action.",
    kind_heartbeat:          "Heartbeat",
    kind_heartbeat_desc:     "Does nothing except write a line to the logs each time it runs. Useful to confirm the scheduler is alive. If you don't know whether you need it, don't use it.",
    unit_seconds:            "seconds",
    unit_minutes:            "minutes",
    unit_hours:              "hours",
    unit_days:               "days",
    every_n_unit:            "every {n} {unit}",
    every_v:                 "every {v}",
    preset_every_10m:        "every 10 min",
    preset_hourly:           "hourly",
    preset_daily_9am:        "daily 9am",
    sched_manual:            "only when you run it",
    preset_weekdays_9am:     "weekdays 9am",
    preset_manual:           "Manual",
    var_pre_output_prompt:   "Text output of the pre-commands. Replaced inside the prompt/text before it is sent. Use it to inject fresh data (weather, an API) into the instruction.",
    var_llm_output:          "Final answer from the agent or super-agent. Available in the post-commands as an env var — e.g. forward it via Telegram.",
    var_status:              "Action result: ok or error. Available in the post-commands to branch on what happened.",
    var_skipped:             "1 if the action was skipped (via skip_prompt_on), 0 if it ran. Available in the post-commands.",
    var_pre_output:          "Full pre-commands output, as an env var in the post-commands (up to 32k).",
    var_pre_output_file:     "Path to a temp file with the pre-commands output. For large outputs not suited to an env var.",
    var_pre_exit:            "Exit code of the last pre-command (0 = ok). Available in the post-commands.",
    var_routine:             "Name of this routine. Available as an env var in the commands.",
    summary_runs_agent:      "Runs the agent \"{agent}\"",
    summary_runs_agent_none: "Runs an agent (none chosen yet)",
    summary_super_agent:     "Calls the super-agent",
    summary_telegram:        "Sends Telegram to \"{channel}\"",
    summary_runs_cmd:        "Runs: {cmd}",
    summary_shell:           "Runs a shell command",
    summary_heartbeat:       "Leaves a heartbeat in the logs",
    action_agent_answers:        "Agent \"{agent}\" answers the prompt",
    action_agent_pick_answers:   "Agent (pick one) answers the prompt",
    action_super_answers:        "The super-agent answers the prompt",
    action_telegram_channel:     "Sends Telegram to channel \"{channel}\"",
    action_runs_shell:           "Runs the shell command",
    step_pre:                "Pre",
    step_post:               "Post",
    last_label:              "last:",
    tg_chat_id_ph:           "(uses the channel's)",
    tg_text_ph:              "message to send",
    hb_message_ph:           "still alive",
    arg_placeholder:         "--flag or value",
    remove_arg:              "remove arg",
    super_agent_label:       "{persona} (super-agent)",
    super_agent_badge:       "super-agent",
  },

  modules_ui: {
    desktop_pos_left:        "Left",
    desktop_pos_center:      "Center",
    desktop_pos_right:       "Right",
    desktop_theme_system:    "System",
    desktop_theme_light:     "Light",
    desktop_theme_dark:      "Dark",
    desktop_status_desc:     "The window launches from the terminal or via autostart.",
    desktop_running:         "Running",
    desktop_stopped:         "Stopped",
    desktop_refresh:         "refresh",
    desktop_start:           "Start",
    desktop_stop:            "Stop",
    desktop_restart:         "Restart",
    desktop_restart_hint:    "Reload the open window so config changes (theme, position) apply now.",
    desktop_restart_done:    "Restarting the window — applying the latest config.",
    desktop_restart_none:    "No desktop window is connected.",
    desktop_start_done:      "Desktop window launched.",
    desktop_start_already:   "Desktop window is already running.",
    desktop_stop_done:       "Desktop window stopped.",
    desktop_stop_none:       "No desktop window was running.",
    desktop_from_terminal:   "From terminal:",
    desktop_autostart_desc:  "Launches the window at user login. Equivalent to `apx desktop install` (no sudo required).",
    desktop_platform:        "platform: {platform}",
    desktop_shortcut_desc:   "Global hotkey that shows/hides the window and starts listening.",
    desktop_accelerator:     "Accelerator",
    desktop_accelerator_hint:"Click the field and press your key combo. Restart the window to apply.",
    desktop_shortcut_record:    "Click to set a shortcut",
    desktop_shortcut_recording: "Press your combo…",
    desktop_shortcut_change:    "click to change",
    desktop_shortcut_esc:       "Esc to cancel",
    desktop_shortcut_saved:  "Shortcut saved. Restart the window (apx desktop stop && start) to apply it.",
    desktop_autostart_on:    "Autostart enabled for the next login.",
    desktop_autostart_off:   "Autostart disabled.",
    desktop_appearance_desc: "Window theme and position on the screen.",
    desktop_theme:           "Theme",
    desktop_restart_apply:   "Restart the window to apply.",
    desktop_theme_set:       "Theme: {value}.",
    desktop_position:        "Position",
    desktop_position_hint:   "\"left\" / \"center\" / \"right\" of the top edge.",
    desktop_position_set:    "Position: {value}.",
    desktop_activation_desc: "The daemon plugin processes the messages. STT is configured in Voices.",
    desktop_enabled_toast:   "Desktop enabled.",
    desktop_disabled_toast:  "Desktop disabled.",
    desktop_plugin_on:       "Plugin enabled (replies to messages)",
    desktop_plugin_off:      "Plugin disabled",
    desktop_stt_engine:      "Speech-to-text engine:",
    desktop_stt_engine_suffix:"(local whisper, language, model).",
    desktop_last_conv_desc:  "The latest exchange with the agent from the floating window.",
    desktop_no_messages:     "No messages yet. Send something to the desktop window for it to appear here.",
    desktop_you:             "You",
    desktop_roby:            "Roby",
    desktop_empty_msg:       "(empty)",
    deck_widget_enabled:     "Widget {id} enabled.",
    deck_widget_disabled:    "Widget {id} disabled.",
    deck_save_error:         "Error while saving",
    deck_loading_manifest:   "Loading manifest…",
    deck_manifest_error:     "Error loading the manifest.",
    deck_widgets_summary:    "{count} widgets · {enabled} external enabled",
    deck_loading_manifest_full:"Loading the Deck manifest…",
    deck_manifest_load_failed:"Could not load the Deck manifest.",
    deck_retry:              "Retry",
    deck_no_widgets:         "No widgets in the manifest.",
    deck_context_desc:       "Information the Deck sees from the daemon.",
    deck_active_project:     "Active project:",
    deck_none:               "none",
    deck_registered_projects:"Registered projects:",
    deck_active_plugins:     "Active plugins:",
    deck_daemon_active:      "active · {uptime}",
    deck_daemon_started:     "started",
    deck_safety_no_shell:    "no direct shell",
    deck_safety_no_arbitrary:"arbitrary commands blocked",
    deck_safety_confirm:     "dangerous actions require confirmation",
    code_copied:             "Copied.",
    code_saved:              "Saved.",
    code_file_empty:         "(empty)",
    code_file_error:         "Error: {msg}",
    code_stream_error:       "error",
    code_super_agent:        "super-agent",
    code_super_agent_desc:   "Main agent with all tools",
    code_chat_tab:           "Chat",
    code_panel_sessions:     "Session list",
    code_panel_tree:         "File tree",
    code_panel_terminal:     "Terminal",
    code_panel_context:      "Context panel",
    code_ctx_auto:           "auto",
    code_ctx_mode:           "Mode",
    code_ctx_agent:          "Agent",
    code_ctx_msgs_value:     "{user} user · {assistant} assistant",
    code_ctx_tokens_total:   "Total Tokens",
    code_ctx_created:        "Created",
    code_ctx_activity:       "Activity",
    code_project_fallback:   "project {id}",
    code_pick_project_ph:    "Pick a project…",
    code_artifact_exit_ok:   "exit 0 — {ms}ms",
    code_artifact_exit_fail: "exit {code}{timeout}",
    code_artifact_timeout_suffix:" (timeout)",
    code_artifact_view_short:"View",
    code_artifact_edit_short:"Edit",
    code_artifact_exit_badge:"exit {code}",
    code_artifact_timeout:   "timeout",
    code_artifact_truncated: "truncated",
  },

  settings_ui: {
    bearer_label:            "Bearer",
    global_config_desc:      "General config in ~/.apx/config.json. Editable by tabs; JSON stays separate.",
    global_json_desc:        "Redacted secrets are not overwritten.",
    save_json:               "Save JSON",
    expand_menu:             "Expand menu",
    collapse_menu:           "Collapse menu",
    documentation:           "Documentation",
    kind_personal:           "Personal",
    kind_company:            "Company",
    kind_app:                "App",
    kind_software:           "Software",
    kind_default:            "Default",
    kind_other:              "Other",
    base_menu_view:          "Base menu view (general workspace).",
    coming_soon:             "Coming soon",
    inspector_title:         "Skill Inspector (per-turn RAG)",
    inspector_desc:          "Experimental feature. When active, the agent does NOT receive the full skill list in its prompt; on each message a local RAG decides which skill(s) to load — the full body on a strong match, a suggestion on a medium match, nothing if it doesn't apply. It is re-evaluated every turn: a skill that stopped being relevant disappears from the context.",
    enable_inspector:        "Enable inspector",
    enable_inspector_hint:   "Off = classic behavior (slug list + passive suggestion). On = the RAG decides per turn.",
    on:                      "On",
    off:                     "Off",
    index_count:             "Index: {n} skills",
    not_indexed:             "not indexed",
    dim:                     "dim {dim}",
    updated_at:              "updated {date}",
    reindex:                 "Reindex",
    reindex_forced:          "Reindex (forced)",
    embedder_source:         "The embedder comes from Memory (RAG). Local with Ollama, or offline if no provider is set.",
    thresholds_title:        "Thresholds and limits",
    thresholds_desc:         "Tune how aggressive the inspector is. Raising the thresholds = fewer false positives but more risk of missing a skill; lowering them = the opposite.",
    test_title:              "Test (dry-run)",
    test_desc:               "Type a message the way a user would and see which skills the inspector would load/suggest — without calling the model. Forces the inspector on even if it's off above.",
    test_placeholder:        "e.g.: I need to create a promo video with voiceover",
    test_btn:                "Test",
    jit_empty_index:         "JIT (empty index)",
    loaded_label:            "Loaded:",
    suggested_label:         "Suggested:",
    could_not_save:          "Could not save: {msg}",
    indexed_with:            "Indexed with {embedder} (dim {dim}): +{added} ~{refreshed} -{removed}.",
    index_failed:            "Index failed: {msg}",
    dry_run_failed:          "Dry-run failed: {msg}",
    knob_load_threshold:        "Load threshold",
    knob_load_threshold_hint:   "Minimum similarity to inject the skill's BODY (high = stricter).",
    knob_hint_threshold:        "Hint threshold",
    knob_hint_threshold_hint:   "Minimum similarity to only SUGGEST the skill (so the agent loads it if it wants).",
    knob_margin:                "Margin over the 2nd",
    knob_margin_hint:           "The top must beat the second by this margin to load its body (avoids weak ties).",
    knob_max_loaded:            "Max. loaded bodies",
    knob_max_loaded_hint:       "How many skills are injected in full per turn.",
    knob_max_hints:             "Max. hints",
    knob_max_hints_hint:        "How many extra skills are named as a suggestion.",
    knob_prompt_floor:          "Minimum prompt length",
    knob_prompt_floor_hint:     "Messages shorter than this are ignored (avoids 'ok', 'hi').",
    knob_body_char_cap:         "Body char cap",
    knob_body_char_cap_hint:    "Trims long skill bodies so they don't bloat the context.",
    cfg_overrides_label:        "Overrides",
    cfg_overrides_desc:         ".apc/config.json. Only project-specific values; empty inherits global/effective.",
    cfg_route_to_agent:         "Route to agent",
    cfg_super_agent_model:      "Super-agent model",
    cfg_permission_mode:        "Permission mode",
    cfg_extra_prompt:           "Extra prompt",
    cfg_telegram_label:         "Telegram",
    cfg_chat_id:                "Chat ID",
    cfg_bot_token:              "Bot token",
    cfg_respond_with_engine:    "Respond with engine",
    cfg_engines_label:          "Engines",
    cfg_ollama_url:             "Ollama URL",
    cfg_anthropic_key:          "Anthropic API key",
    cfg_openai_key:             "OpenAI API key",
    cfg_groq_key:               "Groq API key",
    cfg_openrouter_key:         "OpenRouter API key",
    cfg_gemini_key:             "Gemini API key",
    cfg_project_label:          "Project",
    cfg_project_desc:           ".apc/project.json. Portable APC metadata; no secrets, no runtime.",
    cfg_name:                   "Name",
    cfg_version:                "Version",
    cfg_apc_spec:               "APC spec",
    cfg_apx_install:            "APX install state",
    cfg_apx_storage_id:         "APX storage id",
  },

  skills_page: {
    title:               "Skills",
    desc:                "Turn skills on or off per agent. Pick a scope: the super-agent (global) or a specific project.",
    list_title:          "Installed skills",
    list_desc:           "APX's private skills are always active and can't be changed.",
    scope_label:         "Scope",
    scope_super_agent:   "Super-agent (global)",
    scope_hint:          "The super-agent uses the global scope. Each project can override skills independently.",
    count_label:         "{n} skills · {on} on",
    empty:               "No skills yet. Create one below or install via the CLI.",
    source_builtin:      "APX",
    source_global:       "Global",
    source_project:      "Project",
    private_badge:       "Private",
    private_hint:        "Built-in APX skill — always active, can't be disabled or deleted.",
    overridden_badge:    "Override",
    inherited_hint:      "Inherited from global",
    reset_to_global:     "Reset to global",
    on:                  "on",
    off:                 "off",
    toggle_failed:       "Could not change state: {msg}",
    add_title:           "Add a skill",
    add_desc:            "Creates a user skill at ~/.apx/skills/<slug>/SKILL.md. Available across all scopes.",
    add_slug_label:      "Slug",
    add_slug_ph:         "my-skill",
    add_desc_label:      "Description",
    add_desc_ph:         "One line describing when to use it",
    add_body_label:      "Body (Markdown)",
    add_body_ph:         "# My skill\n\nInstructions for the agent…",
    add_btn:             "Create skill",
    created_ok:          "Skill \"{slug}\" created.",
    create_failed:       "Could not create: {msg}",
    delete_btn:          "Delete",
    delete_confirm:      "Delete skill \"{slug}\"? This can't be undone.",
    deleted_ok:          "Skill \"{slug}\" deleted.",
    delete_failed:       "Could not delete: {msg}",
    inspector_section_title: "Skill Inspector (per-turn RAG)",
    inspector_section_desc:  "Advanced: local RAG that injects only the skills a message needs.",
    scope_ph:            "— choose scope —",
    select_a_skill:      "Pick a skill from the list to see its content.",
    added_by:            "Added by",
    activator:           "Activator",
    by_apx:              "APX (built-in)",
    by_you:              "You",
    activator_value:     "Semantic match (RAG)",
    tab_preview:         "Preview",
    tab_source:          "Source",
    add_menu:            "Add",
    add_online:          "Create with editor",
    add_online_hint:     "Write slug + description + content",
    add_zip:             "Upload .zip",
    add_zip_hint:        "Import a packaged skill",
    add_repo:            "From git repo",
    add_repo_hint:       "Clone from a URL",
    create_dialog_title: "Create skill",
    repo_dialog_title:   "Import from git repo",
    repo_url_label:      "Repo URL",
    repo_url_ph:         "https://github.com/user/my-skill.git",
    repo_url_hint:       "The repo (or its subfolder) must contain a SKILL.md.",
    import_btn:          "Import",
    imported_ok:         "Skill \"{slug}\" imported.",
    import_failed:       "Could not import: {msg}",
    cancel:              "Cancel",
    manager_tab:         "Skills",
    rag_tab:             "Config (RAG)",
  },

  shared_ui: {
    skill_inspector_title: "Skill Inspector ({embedder}) chose these skills for this turn",
    tools_count:           "{n} tools",
    tools_failed:          "{n} failed",
    tool_read_file:        "Read file",
    tool_write_file:       "Write file",
    tool_edit_file:        "Edit file",
    tool_list_files:       "List files",
    tool_search_files:     "Search in files",
    tool_search_messages:  "Search messages",
    tool_tail_messages:    "Latest messages",
    tool_run_shell:        "Run shell",
    tool_send_telegram:    "Send Telegram",
    tool_call_agent:       "Call agent",
    tool_call_mcp:         "Call MCP",
    tool_call_runtime:     "Call runtime",
    tool_create_task:      "Create task",
    dedup:                 "dedup",
    args:                  "args",
    result:                "result",
    auto:                  "Auto",
    auto_router:           "Auto (router decides)",
    model_filter_ph:       "filter or type a model…",
    loading_models:        "loading models…",
    use_value:             "use “{value}”",
    model_combobox_ph:     "pick or type a model…",
    search_variable_ph:    "search variable…",
    no_matches:            "no matches",
    create_variable:       "Create new variable…",
    kv_key_ph:             "KEY",
    kv_value_ph:           "value",
    remove_row:            "remove row",
    add_row:               "Add row",
    err_chat_failed:       "Chat failed.",
    err_stream_failed:     "Stream failed.",
    err_load_conversation: "Could not load conversation.",
    err_stream:            "Stream error.",
  },
  integrations: {
    title:               "Integrations",
    description:         "Plugins and tools available for this project",
    tab_plugins:         "Plugins",
    tab_tools:           "Tools",
    scope_label:         "Scope:",
    scope_project:       "This project",
    scope_global:        "Global (default)",
    plugins_hint:        "Channel & service plugins installable per project. Saved in the scope selected above.",
    more_soon:           "More plugins coming soon…",
    tools_hint:          "Tools that connected plugins expose to this project's agents.",
    tools_empty:         "No integration tools yet. Connect a plugin to enable them.",
    tool_active:         "active",
    tool_inactive:       "inactive",
    status_active:       "Active",
    status_error:        "Error",
    status_unconfigured: "Not configured",
    connected:           "Connected",
    connect:             "Connect",
    deactivate:          "Deactivate",
    saving:              "Saving...",
    validating:          "Validating...",
    verifying:           "Verifying token...",
    confirm:             "Confirm",
    select_placeholder:  "Select...",
    reveal:              "Show",
    hide:                "Hide",
    credentials:         "{name} credentials",
    coming_soon:         "Coming soon",
    coming_soon_body:    "This plugin is declared in the catalog but isn't connectable in APX yet. It will be ported natively in a future iteration.",
    tools_for_agents:    "Agent tools",
    tools_available_note: "Available to agents that allow them, or via discover_tools.",
    err_connect:         "Failed to connect",
    err_generic:         "Something went wrong",
    action_done:         "Done",
    asana: {
      select_label: "Select the workspace to use",
      connected: { user_name: "Connected as", user_email: "Email", workspace_name: "Workspace" },
      fields: {
        personal_access_token: {
          label: "Personal Access Token",
          help_label: "How to get the token?",
          help_steps: "Open app.asana.com/0/my-apps in your browser.\nScroll to the \"Personal access tokens\" section (not your OAuth apps).\nClick \"+ New access token\".\nGive it a name and confirm.\nCopy the full token — it starts with \"1/...\" and has a \":\" in the middle.\nPaste it in the field below.",
        },
      },
    },
    github: {
      connected: { user_login: "Connected as", user_name: "Name" },
      fields: {
        token: {
          label: "Personal Access Token",
          help_label: "How to get the token?",
          help_steps: "Open github.com/settings/tokens.\nGenerate a token (classic or fine-grained) with the \"repo\" scope.\nCopy the token — it starts with ghp_ or github_pat_.\nPaste it in the field below.",
        },
      },
    },
    obsidian: {
      connected: { vault_path: "Vault", vault_name: "Name", note_count: "Notes" },
      fields: {
        vault_path: {
          label: "Vault path",
        },
        auto_mcp: {
          label: "Auto-register Obsidian MCP",
          hint: "Adds an 'obsidian' MCP server pointing at this vault, in this scope.",
        },
        memory_sync: {
          label: "Sync APX memory",
          hint: "Enable mirroring APX memory into the vault, then use the button below.",
        },
      },
      actions: {
        sync_memory: "Sync memory now",
        sync_memory_done: "Synced {count} file(s) · {changed} changed",
      },
    },
  },
} as const;
